import { firebaseApp } from "../config/firebase-config";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";

const FIRESTORE_DB_ID = (process.env.REACT_APP_FIRESTORE_DB_ID || "(default)").trim();
const MATERIALS_COLLECTION =
  (process.env.REACT_APP_MATERIALS_COLLECTION || "materials").trim() || "materials";

const initFirestore = () => {
  try {
    if (FIRESTORE_DB_ID && FIRESTORE_DB_ID !== "(default)") {
      return getFirestore(firebaseApp, FIRESTORE_DB_ID);
    }
    return getFirestore(firebaseApp);
  } catch (error) {
    console.warn(
      `[firebase] Could not initialize Firestore database "${FIRESTORE_DB_ID}", falling back to default.`,
      error
    );
    return getFirestore(firebaseApp);
  }
};

const firestore = initFirestore();
const materialsCollection = collection(firestore, MATERIALS_COLLECTION);

const normalizeMaterialDoc = (snap) => {
  const data = snap.data() || {};
  return {
    id: snap.id,
    name: data.name || "",
    unitPrice:
      typeof data.unitPrice === "number" ? data.unitPrice : Number(data.unitPrice) || 0,
    sourceUrl: data.sourceUrl || "",
    createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt || null,
    updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt || null,
  };
};

const sanitizeMaterialPayload = (payload = {}) => {
  const name = payload.name?.toString().trim() || "";
  if (!name) throw new Error("A nev kotelezo.");
  const unitPriceNumber = Number(payload.unitPrice);
  if (Number.isNaN(unitPriceNumber) || unitPriceNumber <= 0) {
    throw new Error("Az egysegar legyen pozitiv szam.");
  }
  const sourceUrl = payload.sourceUrl?.toString().trim() || "";
  return {
    name,
    unitPrice: unitPriceNumber,
    ...(sourceUrl ? { sourceUrl } : {}),
  };
};

export const createMaterialRecord = async (payload) => {
  const data = sanitizeMaterialPayload(payload);
  const docRef = doc(materialsCollection);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const saved = await getDoc(docRef);
  return normalizeMaterialDoc(saved);
};

export const updateMaterialRecord = async (id, payload) => {
  if (!id) throw new Error("Az anyag azonosito hianyzik.");
  const data = sanitizeMaterialPayload(payload);
  const docRef = doc(materialsCollection, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
  const saved = await getDoc(docRef);
  return normalizeMaterialDoc(saved);
};

export const fetchMaterialsOnce = async () => {
  const q = query(materialsCollection, orderBy("name", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map(normalizeMaterialDoc);
};

export const subscribeToMaterials = (onData, onError) => {
  try {
    const q = query(materialsCollection, orderBy("name", "asc"));
    return onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map(normalizeMaterialDoc);
        onData?.(list);
      },
      (err) => {
        console.error("[firebase] Materials subscription failed:", err);
        onError?.(err);
      }
    );
  } catch (err) {
    console.error("[firebase] Could not subscribe to materials:", err);
    onError?.(err);
    return () => {};
  }
};
